import { Image } from "@tiptap/extension-image";
import type { ImageOptions } from "@tiptap/extension-image";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";

/** A positive plain number: `300`, `'629'`, `'629.5'` — not `'0'`, `'50%'`, `'100%;'`. */
const PIXEL_WIDTH = /^[1-9]\d*(\.\d+)?$/;

interface ResizableImageStorage {
  /** Set between a handle click that left the size unchanged and tiptap's commit. */
  discardResizeCommit: boolean;
}

/**
 * tiptap's `Image` with two changes for scaling at the corner handles
 * (Anke, 2026-09-26, see the `resize` configuration in `rich-text-extensions.ts`).
 *
 * 1. A stored pixel width is also rendered as an inline `style="width: <n>px"`.
 * - View mode renders through `generateHTML`, i.e. `<img width="…">`. The HTML
 *   attribute is only a presentational hint, so any author rule on `width`
 *   beats it — open2done sets `width: fit-content` on every editor image, and
 *   the scaled size would never show. An inline style beats that rule without
 *   touching the host's CSS; the host's `max-width: 100%` still caps it.
 * - Edit mode renders through tiptap's resize NodeView. It copies every
 *   rendered attribute except `src`/`width`/`height` onto the `img`, so the
 *   style lands there too. That is what makes UNDO visible: the NodeView does
 *   not re-apply the size on update, but it does replace or remove `style`
 *   (tiptap 3.30.2, `extension-image/src/image.ts:158, 217-233`).
 * - A pure CSS alternative does not exist: `img[width] { width: auto }` would
 *   override the attribute's hint instead of honouring it, and `attr(width px)`
 *   in `width` works in Chromium only, not across browsers.
 *
 * Only a positive plain number gets the style — also as a string, because the
 * Quill converter stored widths as strings (`'629'`). On srv-999 about 106
 * issue images carry such a Quill width and show in it from now on, mostly
 * smaller than before; that was decided deliberately (Anke, 2026-09-26). Every
 * other value (`'100%;'`, `'50%'`, `'0'`, empty, null) renders as before,
 * without a style. `'0'` is excluded because `width: 0px` would hide the image.
 *
 * 2. A click on a handle without a drag stores nothing.
 * tiptap commits the current size on every mouseup after a mousedown on a
 * handle, and has no way to cancel (`ResizableNodeView.ts:855-863`). A plain
 * click on a handle of an image wider than the editor would store the clamped
 * width and mark the document changed (measured live: 1912 → 668 px). So a
 * press and release on a handle that is a click — the pointer moved less than
 * 4 px, or the image kept its width — drops the one commit transaction that
 * follows (Anke, reviews 0025/01 and 0025/02).
 *
 * The handles themselves carry no CSS from this package — see the comment at
 * the `resize` configuration for the rules a host has to provide.
 */
export const ResizableImage = Image.extend<ImageOptions, ResizableImageStorage>({
  addStorage() {
    return { discardResizeCommit: false };
  },

  addAttributes() {
    return {
      // src, alt, title and height stay exactly as tiptap defines them.
      ...this.parent?.(),
      // Same default as tiptap's own `width`; only the rendering changes.
      width: {
        default: null,
        renderHTML: (attributes: Record<string, unknown>) => {
          const width: unknown = attributes.width;
          if (width === null || width === undefined) {
            return {};
          }
          return PIXEL_WIDTH.test(String(width))
            ? { width, style: `width: ${String(width)}px` }
            : { width };
        },
      },
    };
  },

  addNodeView() {
    const createNodeView = this.parent?.();
    if (!createNodeView) {
      return null;
    }
    const storage = this.storage;
    return (props) => {
      const nodeView = createNodeView(props);
      if (nodeView.dom instanceof HTMLElement) {
        discardClickWithoutDrag(nodeView.dom, storage);
      }
      return nodeView;
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    const imageType = this.name;
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        key: new PluginKey("resizableImageClickWithoutDrag"),
        // What protects other transactions is mainly the window: the flag is
        // set in the mouseup capture listener and cleared right after the event,
        // and within that window only tiptap's commit dispatches (it runs
        // synchronously in tiptap's own mouseup listener; ProseMirror never saw
        // the mousedown, the handle stops its propagation).
        // The selection check is an extra guard, not a precise one: the commit
        // (`chain().setNodeSelection(pos).updateAttributes()`,
        // `extension-image/src/image.ts:258-265`) ends with the image
        // node-selected — only because the image is a block node; `inline: true`
        // would end it with a text selection and bypass this filter. A
        // transaction that ends with another node or a text selection passes,
        // and the flag keeps waiting until it is cleared.
        filterTransaction: (tr) => {
          if (
            !storage.discardResizeCommit ||
            !(tr.selection instanceof NodeSelection) ||
            tr.selection.node.type.name !== imageType
          ) {
            return true;
          }
          storage.discardResizeCommit = false;
          return false;
        },
      }),
    ];
  },
});

/** Pointer travel below which a press and release on a handle counts as a click. */
const CLICK_TOLERANCE_PX = 4;

/**
 * Arms `storage.discardResizeCommit` when a mousedown on one of the container's
 * handles is followed by a mouseup that is a click rather than a resize: the
 * pointer moved less than {@link CLICK_TOLERANCE_PX} (a hand trembles by a
 * pixel or two, and tiptap resizes on every pixel), or the image ended up at
 * the width it had on mousedown. Either way the inline size tiptap may already
 * have applied is reset, so the image shows what the document still says — a
 * drag outward on an image the editor already clamps leaves an inline width
 * wider than the editor, which would show once the editor gets wider.
 *
 * The mousedown listener sits on the container in the capture phase for two
 * reasons: tiptap's own mousedown listener on the handle calls
 * `stopPropagation()` (`ResizableNodeView.ts:754`), so a bubble listener on the
 * container would never run; and tiptap removes and re-creates the handles when
 * the editor's editability changes, so a listener on a handle could be lost.
 * The mouseup listener sits on `document` in the capture phase, so it runs
 * before tiptap's own mouseup listener on `document` commits. The flag is
 * cleared again after the event, so a commit that never comes cannot swallow a
 * later one.
 */
function discardClickWithoutDrag(
  container: HTMLElement,
  storage: ResizableImageStorage,
): void {
  container.addEventListener(
    "mousedown",
    (down) => {
      const img = container.querySelector("img");
      if (!img || !(down.target instanceof Element) || !down.target.closest("[data-resize-handle]")) {
        return;
      }
      // Width only: the aspect ratio is always kept, so the width alone says
      // whether the image changed. The height can differ without a change —
      // tiptap sets it inline while `max-width` clamps the width, and a host
      // without `height: auto !important` shows that.
      const width = img.offsetWidth;
      const styleWidth = img.style.width;
      const styleHeight = img.style.height;
      document.addEventListener(
        "mouseup",
        (up) => {
          const travel = Math.hypot(up.clientX - down.clientX, up.clientY - down.clientY);
          const isClick = travel < CLICK_TOLERANCE_PX;
          if (!isClick && img.offsetWidth !== width) {
            return;
          }
          img.style.width = styleWidth;
          img.style.height = styleHeight;
          storage.discardResizeCommit = true;
          setTimeout(() => {
            storage.discardResizeCommit = false;
          }, 0);
        },
        { capture: true, once: true },
      );
    },
    { capture: true },
  );
}
